import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";

// Eager — marketing + auth shell (tiny critical bundle, loads fast on 2G)
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ThemePreview from "@/pages/ThemePreview";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";

// Lazy — dashboards (not needed on first paint; code-split per role)
const DashboardLayout = lazy(() => import("@/components/DashboardLayout"));

const AdminDashboard = lazy(() => import("@/pages/admin/Dashboard"));
const AdminGuests = lazy(() => import("@/pages/admin/Guests"));
const AdminBusinesses = lazy(() => import("@/pages/admin/Businesses"));
const AdminPlans = lazy(() => import("@/pages/admin/Plans"));
const AdminTransactions = lazy(() => import("@/pages/admin/Transactions"));
const AdminPayments = lazy(() => import("@/pages/admin/Payments"));
const AdminApiKeys = lazy(() => import("@/pages/admin/ApiKeys"));
const AdminApiDocs = lazy(() => import("@/pages/admin/ApiDocs"));
const AdminCategories = lazy(() => import("@/pages/admin/Categories"));
const AdminSettings = lazy(() => import("@/pages/admin/Settings"));
const AdminProfile = lazy(() => import("@/pages/admin/Profile"));
const AdminSalesPartners = lazy(() => import("@/pages/admin/SalesPartners"));
const AdminAdmins = lazy(() => import("@/pages/admin/Admins"));
const AdminAuditLogs = lazy(() => import("@/pages/admin/AuditLogs"));

const SalesPartnerApply = lazy(() => import("@/pages/SalesPartnerApply"));
const SalesDashboard = lazy(() => import("@/pages/sales/Dashboard"));
const SalesBusinesses = lazy(() => import("@/pages/sales/Businesses"));

const GuestDashboard = lazy(() => import("@/pages/guest/Dashboard"));
const GuestSubscription = lazy(() => import("@/pages/guest/Subscription"));
const GuestKyc = lazy(() => import("@/pages/guest/Kyc"));
const GuestTransactions = lazy(() => import("@/pages/guest/Transactions"));
const GuestQR = lazy(() => import("@/pages/guest/QRCode"));
const GuestProfile = lazy(() => import("@/pages/guest/Profile"));
const GuestDiscover = lazy(() => import("@/pages/guest/Discover"));
const GuestReferrals = lazy(() => import("@/pages/guest/Referrals"));
const AdminPoolValue = lazy(() => import("@/pages/admin/PoolValue"));
const NotificationsPage = lazy(() => import("@/pages/Notifications"));
const GuestWallet = lazy(() => import("@/pages/guest/Wallet"));

const BizDashboard = lazy(() => import("@/pages/business/Dashboard"));
const BizScanner = lazy(() => import("@/pages/business/Scanner"));
const BizTransactions = lazy(() => import("@/pages/business/Transactions"));
const BizReviews = lazy(() => import("@/pages/business/Reviews"));
const BizProfile = lazy(() => import("@/pages/business/Profile"));
const BizOffers = lazy(() => import("@/pages/business/Offers"));
const BizOfferEdit = lazy(() => import("@/pages/business/OfferEdit"));
const BizPhotos = lazy(() => import("@/pages/business/Photos"));

function RouteSkeleton() {
  return (
    <div className="min-h-screen bg-background noise-bg flex items-center justify-center p-6" data-testid="route-skeleton">
      <div className="flex items-center gap-3">
        <div className="w-2.5 h-2.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: "0ms" }} />
        <div className="w-2.5 h-2.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: "120ms" }} />
        <div className="w-2.5 h-2.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: "240ms" }} />
        <span className="sr-only">Loading…</span>
      </div>
    </div>
  );
}

function RoleRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <RouteSkeleton />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role === "business") return <Navigate to="/business" replace />;
  if (user.role === "sales_partner") return <Navigate to="/sales" replace />;
  return <Navigate to="/guest" replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<RouteSkeleton />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/theme-preview" element={<ThemePreview />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/sales-partner/apply" element={<SalesPartnerApply />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/app" element={<RoleRedirect />} />

            <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="guests" element={<AdminGuests />} />
              <Route path="businesses" element={<AdminBusinesses />} />
              <Route path="sales-partners" element={<AdminSalesPartners />} />
              <Route path="plans" element={<AdminPlans />} />
              <Route path="pool-value" element={<AdminPoolValue />} />
              <Route path="categories" element={<AdminCategories />} />
              <Route path="transactions" element={<AdminTransactions />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="api-keys" element={<AdminApiKeys />} />
              <Route path="api-docs" element={<AdminApiDocs />} />
              <Route path="admins" element={<AdminAdmins />} />
              <Route path="audit-logs" element={<AdminAuditLogs />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="profile" element={<AdminProfile />} />
            </Route>

            <Route path="/sales" element={<ProtectedRoute roles={["sales_partner"]}><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<SalesDashboard />} />
              <Route path="businesses" element={<SalesBusinesses />} />
            </Route>

            <Route path="/guest" element={<ProtectedRoute roles={["guest"]}><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<GuestDashboard />} />
              <Route path="qr" element={<GuestQR />} />
              <Route path="discover" element={<GuestDiscover />} />
              <Route path="subscription" element={<GuestSubscription />} />
              <Route path="kyc" element={<GuestKyc />} />
              <Route path="transactions" element={<GuestTransactions />} />
              <Route path="wallet" element={<GuestWallet />} />
              <Route path="referrals" element={<GuestReferrals />} />
              <Route path="profile" element={<GuestProfile />} />
            </Route>

            <Route path="/business" element={<ProtectedRoute roles={["business"]}><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<BizDashboard />} />
              <Route path="scan" element={<BizScanner />} />
              <Route path="offers" element={<BizOffers />} />
              <Route path="offers/new" element={<BizOfferEdit />} />
              <Route path="offers/:oid/edit" element={<BizOfferEdit />} />
              <Route path="photos" element={<BizPhotos />} />
              <Route path="transactions" element={<BizTransactions />} />
              <Route path="reviews" element={<BizReviews />} />
              <Route path="profile" element={<BizProfile />} />
            </Route>

            <Route path="/notifications" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<NotificationsPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        <Toaster richColors position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}
